import { useEffect, useMemo, useReducer } from 'react';
import {
  getP32Templates,
  P28_TEMPLATES,
  P3_TEMPLATES,
  P5_TEMPLATES,
  type Channel,
  type ChannelTemplate,
} from '@lmt/signal-viewer-ui';
import { LocalStorageKeys } from '../local-storage';
import {
  applyChannelOrder,
  readChannelViewFromStorage,
  writeChannelViewToStorage,
  type ChannelView,
  type ChannelViewByProfileId,
} from './channel-view';

export type ProfileGeneration = 'p28' | 'p3' | 'p32' | 'p5';

export type Profile = {
  id: string;
  name: string;
  config: Channel[];
  generation?: ProfileGeneration;
};

export const PredefinedProfileIds = {
  P28: 'p28-profile-visconfig',
  P28Demo: 'p28-demo-profile-visconfig',
  P3: 'p3-profile-visconfig',
  P3Demo: 'p3-demo-profile-visconfig',
  P32: 'p32-profile-visconfig',
  P32Demo: 'p32-demo-profile-visconfig',
  P5: 'p5-profile-visconfig',
  P5Demo: 'p5-demo-profile-visconfig',
};

const DEMO_PALETTE = [
  '#cb69ac',
  '#003594',
  '#4ea0d5',
  '#ffffca',
  '#093668',
  '#e8bdda',
  '#41afad',
  '#5b697d',
  '#cb69ac',
] as const;

const PREDEFINED_ID_SET = new Set<string>(Object.values(PredefinedProfileIds));

export function isPredefinedProfileId(id: string) {
  return PREDEFINED_ID_SET.has(id);
}

export function makeId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isProfile(value: unknown): value is Profile {
  if (!value || typeof value !== 'object') return false;

  const v = value as Record<string, unknown>;

  return (
    typeof v.id === 'string' &&
    typeof v.name === 'string' &&
    Array.isArray(v.config)
  );
}

function isProfilesArray(value: unknown): value is Profile[] {
  return Array.isArray(value) && value.every(isProfile);
}

function readProfilesFromStorage(): Profile[] {
  const saved = localStorage.getItem(LocalStorageKeys.Specs);
  if (!saved) return [];

  try {
    const parsed = JSON.parse(saved);

    if (isProfilesArray(parsed)) {
      return parsed;
    }

    localStorage.removeItem(LocalStorageKeys.Specs);
    return [];
  } catch {
    localStorage.removeItem(LocalStorageKeys.Specs);
    return [];
  }
}

function writeProfilesToStorage(profiles: Profile[]) {
  localStorage.setItem(LocalStorageKeys.Specs, JSON.stringify(profiles));
}

function readCurrentProfileIdFromStorage(): string | null {
  return localStorage.getItem(LocalStorageKeys.CurrentProfileId);
}

function writeCurrentProfileIdToStorage(profileId: string) {
  localStorage.setItem(LocalStorageKeys.CurrentProfileId, profileId);
}

type State = {
  profiles: Profile[];
  currentProfileId: string;
  viewByProfileId: ChannelViewByProfileId;
};

type Action =
  | { type: 'switch'; id: string; generation?: ProfileGeneration }
  | { type: 'delete'; id: string }
  | { type: 'save'; profile: Profile }
  | { type: 'reorder'; profileId: string; channelIds: string[] }
  | { type: 'hide'; profileId: string; channelId: string; hidden: boolean };

const buildPredefinedProfiles = (): Profile[] => [
  createP28Profile(),
  createP28DemoProfile(),
  createP3Profile(),
  createP3DemoProfile(),
  createP32Profile(),
  createP32DemoProfile(),
  createP5Profile(),
  createP5DemoProfile(),
];

const configFingerprint = (config: Channel[]) =>
  JSON.stringify([...config].sort((a, b) => a.id.localeCompare(b.id)));

const isContentChanged = (stored: Profile, edited: Profile) =>
  stored.name !== edited.name ||
  configFingerprint(stored.config) !== configFingerprint(edited.config);

const forkName = (profiles: Profile[], stored: Profile, edited: Profile) => {
  const desired =
    edited.name !== stored.name ? edited.name : `${stored.name} (Custom)`;
  const taken = new Set(profiles.map((p) => p.name));

  if (!taken.has(desired)) return desired;

  let suffix = 2;
  while (taken.has(`${desired} ${suffix}`)) suffix += 1;

  return `${desired} ${suffix}`;
};

const withView = (
  viewByProfileId: ChannelViewByProfileId,
  profileId: string,
  view: ChannelView,
): ChannelViewByProfileId => ({ ...viewByProfileId, [profileId]: view });

function init(): State {
  const customProfiles = readProfilesFromStorage().filter(
    (p) => !isPredefinedProfileId(p.id),
  );
  const profiles = [...buildPredefinedProfiles(), ...customProfiles];

  const savedCurrentProfileId = readCurrentProfileIdFromStorage();
  const hasSavedCurrentProfile = profiles.some(
    (p) => p.id === savedCurrentProfileId,
  );
  const shouldUseSavedProfile =
    hasSavedCurrentProfile &&
    savedCurrentProfileId !== PredefinedProfileIds.P32;

  return {
    profiles,
    currentProfileId: shouldUseSavedProfile
      ? (savedCurrentProfileId as string)
      : PredefinedProfileIds.P28,
    viewByProfileId: readChannelViewFromStorage(),
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'switch': {
      const existing = state.profiles.some((p) => p.id === action.id);

      if (existing) {
        return { ...state, currentProfileId: action.id };
      }

      const created: Profile = {
        id: action.id,
        name: 'New Profile',
        config: [],
        generation: action.generation,
      };

      return {
        ...state,
        profiles: [...state.profiles, created],
        currentProfileId: created.id,
      };
    }

    case 'delete': {
      if (isPredefinedProfileId(action.id)) return state;

      const rest = state.profiles.filter((p) => p.id !== action.id);
      const viewByProfileId = { ...state.viewByProfileId };
      delete viewByProfileId[action.id];

      const nextId =
        state.currentProfileId === action.id
          ? rest[0].id
          : state.currentProfileId;

      return { profiles: rest, currentProfileId: nextId, viewByProfileId };
    }

    case 'save': {
      const idx = state.profiles.findIndex((p) => p.id === action.profile.id);
      if (idx === -1) return state;

      const stored = state.profiles[idx];
      const view: ChannelView = {
        ...state.viewByProfileId[stored.id],
        order: action.profile.config.map((channel) => channel.id),
      };

      if (isPredefinedProfileId(stored.id)) {
        if (!isContentChanged(stored, action.profile)) {
          return {
            ...state,
            viewByProfileId: withView(state.viewByProfileId, stored.id, view),
          };
        }

        const fork: Profile = {
          ...action.profile,
          id: makeId(),
          name: forkName(state.profiles, stored, action.profile),
        };

        return {
          profiles: [...state.profiles, fork],
          currentProfileId: fork.id,
          viewByProfileId: withView(state.viewByProfileId, fork.id, view),
        };
      }

      const profiles = state.profiles.slice();
      profiles[idx] = action.profile;

      return {
        ...state,
        profiles,
        viewByProfileId: withView(state.viewByProfileId, stored.id, view),
      };
    }

    case 'reorder':
      return {
        ...state,
        viewByProfileId: withView(state.viewByProfileId, action.profileId, {
          ...state.viewByProfileId[action.profileId],
          order: action.channelIds,
        }),
      };

    case 'hide': {
      const view = state.viewByProfileId[action.profileId];
      const hidden = new Set(view?.hidden ?? []);

      if (action.hidden) hidden.add(action.channelId);
      else hidden.delete(action.channelId);

      return {
        ...state,
        viewByProfileId: withView(state.viewByProfileId, action.profileId, {
          ...view,
          hidden: [...hidden],
        }),
      };
    }
  }
}

// Stable identity so consumers can depend on it without re-running effects.
const EMPTY_CHANNEL_IDS: string[] = [];

export function useChannelsProfileStorage() {
  const [state, dispatch] = useReducer(reducer, undefined, init);

  useEffect(() => {
    writeProfilesToStorage(
      state.profiles.filter((p) => !isPredefinedProfileId(p.id)),
    );
  }, [state.profiles]);

  useEffect(() => {
    writeCurrentProfileIdToStorage(state.currentProfileId);
  }, [state.currentProfileId]);

  useEffect(() => {
    writeChannelViewToStorage(state.viewByProfileId);
  }, [state.viewByProfileId]);

  const storedProfile = state.profiles.find(
    (p) => p.id === state.currentProfileId,
  )!;
  const view = state.viewByProfileId[state.currentProfileId];

  const currentProfile = useMemo<Profile>(
    () => ({
      ...storedProfile,
      config: applyChannelOrder(storedProfile.config, view?.order),
    }),
    [storedProfile, view?.order],
  );

  return {
    profiles: state.profiles,
    currentProfile,
    currentProfileId: state.currentProfileId,
    hiddenChannelIds: view?.hidden ?? EMPTY_CHANNEL_IDS,

    switchProfile: (id: string, generation?: ProfileGeneration) =>
      dispatch({ type: 'switch', id, generation }),
    deleteProfile: (id: string) => dispatch({ type: 'delete', id }),
    saveProfile: (profile: Profile) => dispatch({ type: 'save', profile }),
    setChannelOrder: (channelIds: string[]) =>
      dispatch({
        type: 'reorder',
        profileId: state.currentProfileId,
        channelIds,
      }),
    setChannelHidden: (channelId: string, hidden: boolean) =>
      dispatch({
        type: 'hide',
        profileId: state.currentProfileId,
        channelId,
        hidden,
      }),
  };
}

export const convertTemplatesToProfile = (
  id: string,
  name: string,
  templates: ChannelTemplate[],
  generation: ProfileGeneration,
): Profile => {
  return {
    id,
    name,
    config: templates.map((template) => template.channel),
    generation,
  };
};

function recolorTemplates(templates: ChannelTemplate[]): ChannelTemplate[] {
  let colorIndex = 0;
  const nextColor = () => {
    const color = DEMO_PALETTE[colorIndex % DEMO_PALETTE.length];
    colorIndex += 1;
    return color;
  };

  return templates.map((template) => ({
    ...template,
    channel: {
      ...template.channel,
      signals: template.channel.signals.map(
        (signal: Channel['signals'][number]) => ({
          ...signal,
          config: {
            ...signal.config,
            color: nextColor(),
          },
        }),
      ),
      events: template.channel.events.map(
        (event: Channel['events'][number]) => {
          const fillColor = nextColor();
          const borderColor = nextColor();
          return {
            ...event,
            config: {
              ...event.config,
              fillColor,
              borderColor,
            },
          };
        },
      ),
    },
  }));
}

export function createP28Profile(): Profile {
  return convertTemplatesToProfile(
    PredefinedProfileIds.P28,
    'P2.8 Default',
    P28_TEMPLATES,
    'p28',
  );
}

export function createP28DemoProfile(): Profile {
  return convertTemplatesToProfile(
    PredefinedProfileIds.P28Demo,
    'P2.8 Demo',
    recolorTemplates(P28_TEMPLATES),
    'p28',
  );
}

export function createP3Profile(): Profile {
  return convertTemplatesToProfile(
    PredefinedProfileIds.P3,
    'P3 Default',
    P3_TEMPLATES,
    'p3',
  );
}

export function createP3DemoProfile(): Profile {
  return convertTemplatesToProfile(
    PredefinedProfileIds.P3Demo,
    'P3 Demo',
    recolorTemplates(P3_TEMPLATES),
    'p3',
  );
}

export function createP32Profile(): Profile {
  return convertTemplatesToProfile(
    PredefinedProfileIds.P32,
    'P3.2 Default',
    getP32Templates(),
    'p32',
  );
}

export function createP32DemoProfile(): Profile {
  return convertTemplatesToProfile(
    PredefinedProfileIds.P32Demo,
    'P3.2 Demo',
    recolorTemplates(getP32Templates()),
    'p32',
  );
}

export function createP5Profile(): Profile {
  return convertTemplatesToProfile(
    PredefinedProfileIds.P5,
    'P5 Default',
    P5_TEMPLATES,
    'p5',
  );
}

export function createP5DemoProfile(): Profile {
  return convertTemplatesToProfile(
    PredefinedProfileIds.P5Demo,
    'P5 Demo',
    recolorTemplates(P5_TEMPLATES),
    'p5',
  );
}
